import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import GlassCard from '../components/ui/GlassCard';
import SectionLabel from '../components/ui/SectionLabel';
import ProjectModal from '../components/ui/ProjectModal';
import TechnicalAnnotation from '../components/scene/TechnicalAnnotation';
import { useChapterScene } from '../context/SceneContext';
import { StatusPill, DeploymentBadge, TelemetryRow } from '../components/scene/Telemetry';

/* Blur-reveal wrapper */
const BlurReveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 28, filter: 'blur(10px)' }}
    whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* 3D tilt card */
function TiltCard({ children, className = '' }) {
  const ref = useRef(null);

  const handleMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;
    el.style.transform = `perspective(900px) rotateY(${x * 7}deg) rotateX(${-y * 5}deg) scale(1.015)`;
  }, []);

  const handleLeave = useCallback(() => {
    if (ref.current) ref.current.style.transform = 'perspective(900px) rotateY(0deg) rotateX(0deg) scale(1)';
  }, []);

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={className}
      style={{ transformStyle: 'preserve-3d', transition: 'transform 0.15s ease, box-shadow 0.3s ease' }}
    >
      {children}
    </div>
  );
}

const projects = [
  {
    id: 'electra',
    title: 'Electra — AI-Powered Energy Monitoring',
    year: '2024–2025',
    description: 'AI/ML energy intelligence platform converting raw IoT signals into closed-loop optimization directives — achieving 12–18% operational waste reduction across pilot deployments.',
    tech: ['Python', 'IoT', 'Power BI', 'Machine Learning'],
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&h=400&fit=crop',
    metrics: { savings: '12-18%', impact: 'Energy Waste Reduction' },
    telemetry: { latency: 'sub-120ms', signals: '50k/day', uptime: '99.7%' },
  },
  {
    id: 'tetra',
    title: 'Assistive Device — Tetra Society',
    year: '2024',
    description: 'Precision assistive hardware engineered from first principles around user biomechanics — compressing delivery cycles and cutting actuation effort by 35% for mobility-impaired users.',
    tech: ['SolidWorks', 'Arduino', 'FDM Printing', 'Ergonomics'],
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&h=400&fit=crop',
    metrics: { savings: '35%', impact: 'Effort Reduction' },
    telemetry: { cycle: '3-week', iterations: '8', effort: '−35%' },
  },
  {
    id: '3d-design',
    title: '3D Mechanical & Product Design',
    year: '2023–2025',
    description: 'Parametric mechanical systems built for manufacturability at every stage — 50+ precision components shipped with a 22% reduction in downstream DFM issues.',
    tech: ['SolidWorks', 'Fusion 360', 'KeyShot', 'CAD'],
    image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&h=400&fit=crop',
    metrics: { savings: '22%', impact: 'DFM Issue Reduction' },
    telemetry: { components: '50+', dfm: '−22%', tolerance: '±0.05mm' },
  },
];

const experiences = [
  {
    role: 'Program Coordinator',
    company: 'Tetra Society of North America',
    period: '2024 — Present',
    highlights: [
      'Rebuilt the assistive-device engineering pipeline from first principles—collapsing 12-week delivery cycles to 3, sustaining a 10-person cross-functional team at zero turnover, and deploying parametric CAD and FDM rapid-prototyping systems that cut design iteration time by 40% and were adopted as the operational standard across 3 regional chapters.',
    ],
  },
  {
    role: 'AI/ML Research Engineer',
    company: 'University of Ottawa',
    period: '01.2024 — Present',
    highlights: [
      'Architected a full-stack ML energy intelligence platform from zero to commercial scale—engineering real-time BI pipelines processing 50K+ IoT signals daily at sub-120ms latency, achieving 12–18% operational waste reduction across pilot deployments, and executing a zero-to-one GTM strategy that closed 3 enterprise pilots within 6 months of launch.',
    ],
  },
  {
    role: 'Cloud Security Intern',
    company: 'Amazon',
    period: '10.2023 — 06.2024',
    highlights: [
      'Hardened AWS security posture at Amazon scale—rearchitecting IAM permission boundaries and CloudTrail detection pipelines to cut mean-time-to-detect by 20%, co-authoring incident-response runbooks that eliminated compounding configuration drift across production accounts, and shipping automated compliance-scanning workflows that reclaimed 15+ analyst hours weekly for higher-signal threat hunting.',
    ],
  },
  {
    role: 'Cyber Security Analyst',
    company: 'Microsoft',
    period: '06.2022 — 02.2024',
    highlights: [
      'Engineered enterprise-grade Zero Trust security architecture across distributed Azure cloud environments—reducing attack surface exposure by 31% across tenants at scale, directing threat modelling across 40+ cloud workloads to identify and contain 3 high-severity incidents before breach, and automating Defender for Cloud compliance pipelines that eliminated 60% of manual audit overhead while sustaining 99.4% policy adherence.',
    ],
  },
];

// Skills matching the screenshot - 8 categories
const skillCategories = [
  {
    title: 'Strategy',
    skills: ['Roadmapping', 'Market Analysis', 'OKRs & KPIs', 'Go-to-Market', 'Growth', 'Product Vision'],
  },
  {
    title: 'Execution',
    skills: ['Agile / Scrum', 'Jira / Confluence', 'Gantt & WBS', 'Budget Control', 'Risk Management', 'QA'],
  },
  {
    title: 'Technical',
    skills: ['Python / SQL', 'Power BI / Tableau', 'IoT / Edge', 'AI/ML (Applied)', 'API Integration', 'System Design'],
  },
  {
    title: 'Engineering',
    skills: ['SolidWorks / Fusion 360', 'AutoCAD', 'FEA & Thermal', 'GD&T', 'DFM', '3D Modelling'],
  },
  {
    title: 'Energy & Sustainability',
    skills: ['Energy Auditing', 'HVAC Systems', 'Renewable Integration', 'ESG Reporting', 'Carbon Analysis', 'Green Standards'],
  },
  {
    title: 'Leadership',
    skills: ['Stakeholder Mgmt', 'Cross-functional Teams', 'Mentorship', 'Board Presentations', 'Talent Development', 'Crisis Management'],
  },
  {
    title: 'Process & Quality',
    skills: ['Lean Six Sigma', 'ISO 9001 / 13485', 'FMEA / RCA', 'SOP Development', 'Audit Management', 'Compliance'],
  },
  {
    title: 'Construction & Site',
    skills: ['Site Supervision', 'EHS Compliance', 'Subcontractor Mgmt', 'Inspection', 'Building Codes', 'LEED Fundamentals'],
  },
];

const tools = [
  { name: 'AI / ML Engineering', level: 96 },
  { name: 'Python', level: 95 },
  { name: 'Network Security', level: 93 },
  { name: 'Power BI', level: 94 },
  { name: 'Jira / Confluence', level: 95 },
  { name: 'Cloud (AWS / Azure)', level: 93 },
  { name: 'Agile / Scrum', level: 97 },
  { name: 'Cybersecurity', level: 94 },
  { name: 'Data Analytics', level: 96 },
  { name: 'Product Strategy', level: 95 },
  { name: 'SolidWorks', level: 96 },
  { name: 'System Design', level: 93 },
];

export default function ProjectsPage() {
  const [selectedProject, setSelectedProject] = useState(null);
  const projectsRef = useChapterScene('mutation');
  const fieldOpsRef = useChapterScene('field-ops');
  const arsenalRef = useChapterScene('arsenal');

  return (
    <div className="relative pt-24" data-testid="projects-page">
      {/* Content */}
      <div className="relative z-10">
      {/* Page Hero */}
        <section ref={projectsRef} className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
          <BlurReveal>
            <div className="mb-6">
              <TechnicalAnnotation code="CH · 01 / DEPLOYMENTS" title="Production-shipped systems" />
            </div>
          </BlurReveal>

          <BlurReveal delay={0.15}>
            <h1
              className="font-headline text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter uppercase mt-2 mb-4 leading-[0.95]"
              data-testid="projects-title"
            >
              Deployed <span className="gradient-text">Work</span>
            </h1>
          </BlurReveal>

          <BlurReveal delay={0.28}>
            <p className="text-base sm:text-lg text-white/45 max-w-2xl font-rajdhani leading-relaxed">
              Every system below was shipped to production. Not a side project, not a concept — work that runs, scales, and survives contact with reality.
            </p>
          </BlurReveal>
        </section>

        {/* Projects Grid */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
          <div className="grid grid-cols-1 gap-8">
            {projects.map((project, index) => (
              <BlurReveal key={project.id} delay={0.1 * index}>
                <TiltCard>
                  <GlassCard
                    className="p-0 overflow-hidden hover:border-cyan-500/25 transition-colors duration-500"
                    data-testid={`project-card-${project.id}`}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                      {/* Image Side */}
                      <div className="relative h-64 lg:h-auto overflow-hidden">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
                        {/* Year Badge — top-left */}
                        <div className="absolute top-4 left-4 px-3 py-1 bg-black/60 backdrop-blur-sm border border-[var(--hyper-cyan)]/40 font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--hyper-cyan)]">
                          {project.year}
                        </div>
                        {/* Telemetry stack — top-right */}
                        <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5">
                          <StatusPill status="active" label="status: active" />
                          <DeploymentBadge verified />
                        </div>
                      </div>

                      {/* Content Side */}
                      <div className="p-8 lg:p-10 flex flex-col justify-between">
                        <div>
                          <h3 className="font-headline text-2xl md:text-3xl font-black tracking-tight mb-4 leading-[1.05] group-hover:text-[var(--hyper-cyan)] transition-colors">
                            {project.title}
                          </h3>
                          <p className="text-white/60 font-rajdhani text-lg leading-relaxed mb-5">
                            {project.description}
                          </p>

                          {/* Telemetry readout */}
                          <div className="border-t border-white/8 pt-3 mb-5 space-y-1.5">
                            {Object.entries(project.telemetry).map(([k, v]) => (
                              <TelemetryRow key={k} label={k} value={v} accent={k === 'latency' || k === 'dfm' || k === 'effort'} />
                            ))}
                          </div>

                          {/* Tech Stack */}
                          <div className="flex flex-wrap gap-2 mb-6">
                            {project.tech.map((tech) => (
                              <span
                                key={tech}
                                className="px-2.5 py-0.5 font-mono text-[9px] tracking-[0.22em] uppercase bg-white/5 border border-white/10 text-white/65 hover:border-[var(--hyper-cyan)]/50 hover:text-white/90 transition-all duration-200"
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Metrics */}
                        <div className="flex items-center justify-between pt-5 border-t border-white/10">
                          <div>
                            <div className="font-headline text-3xl font-black text-[var(--hyper-cyan)] tracking-tight">
                              {project.metrics.savings}
                            </div>
                            <div className="font-mono text-[9px] text-white/40 tracking-[0.22em] uppercase">
                              {project.metrics.impact}
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedProject(project.id)}
                            className="w-12 h-12 rounded-full border border-[var(--hyper-cyan)]/35 flex items-center justify-center hover:bg-[var(--hyper-cyan)]/12 hover:border-[var(--hyper-cyan)] hover:shadow-[0_0_22px_rgba(0,240,255,0.45)] transition-all duration-300 cursor-pointer"
                            data-testid={`project-link-${project.id}`}
                          >
                            <ArrowUpRight size={20} className="text-[var(--hyper-cyan)]" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </GlassCard>
                </TiltCard>
              </BlurReveal>
            ))}
          </div>
        </section>

        {/* Field Operations */}
        <section ref={fieldOpsRef} className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
          <BlurReveal>
            <div className="mb-6">
              <TechnicalAnnotation code="CH · 02 / FIELD OPS" title="Engagements stress-tested against reality" />
            </div>
          </BlurReveal>

          <BlurReveal delay={0.15}>
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter uppercase mt-2 mb-3 leading-[0.95]">
              Field <span className="gradient-text">Operations</span>
            </h2>
          </BlurReveal>

          <BlurReveal delay={0.22}>
            <p className="text-lg text-white/35 max-w-2xl font-rajdhani leading-relaxed mb-12">
              Where the architecture was stress-tested against reality.
              Each engagement demanded a different discipline. All demanded results.
            </p>
          </BlurReveal>

          <div className="space-y-6">
            {experiences.map((exp, index) => (
              <BlurReveal key={exp.role} delay={0.1 * index}>
                <GlassCard
                  className="p-8 hover:border-white/15 transition-colors duration-500"
                  data-testid={`experience-card-${index}`}
                >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                  <div>
                    <h3 className="font-orbitron text-xl font-bold text-white">
                      {exp.role}
                    </h3>
                    <p className="text-cyan-400 font-rajdhani tracking-wider">
                      {exp.company}
                    </p>
                  </div>
                  <span className="text-white/40 font-rajdhani text-sm tracking-wider shrink-0">
                    {exp.period}
                  </span>
                </div>
                
                <ul className="space-y-4">
                  {exp.highlights.map((highlight, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/70 font-rajdhani leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-2.5 shrink-0" />
                      {highlight}
                    </li>
                  ))}
                </ul>
                </GlassCard>
              </BlurReveal>
            ))}
          </div>

          {/* ── Availability Banner ──────────────────────────── */}
          <BlurReveal delay={0.25} className="mt-8">
            <Link to="/contact" data-testid="availability-banner">
              <div className="relative overflow-hidden border border-cyan-500/25 bg-gradient-to-r from-cyan-500/5 via-black/40 to-cyan-500/5 hover:border-cyan-500/55 hover:from-cyan-500/10 hover:via-black/50 hover:to-cyan-500/10 transition-all duration-500 cursor-pointer group px-5 sm:px-8 py-4 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                {/* Animated shimmer sweep */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/6 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out pointer-events-none" />
                {/* Left side */}
                <div className="flex items-center gap-3 sm:gap-5 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full bg-cyan-400 shrink-0 animate-pulse"
                    style={{ boxShadow: '0 0 10px rgba(0,212,255,0.7), 0 0 20px rgba(0,212,255,0.3)' }}
                  />
                  <span className="font-orbitron text-[11px] sm:text-sm tracking-[0.2em] sm:tracking-[0.25em] uppercase text-cyan-400 shrink-0">
                    Currently Available
                  </span>
                  <span className="hidden sm:block w-px h-4 bg-white/15 shrink-0" />
                  <span className="hidden sm:block font-rajdhani text-white/45 text-sm tracking-wide truncate">
                    Open to product leadership, engineering strategy &amp; senior IC roles
                  </span>
                </div>
                {/* Right side CTA */}
                <div className="flex items-center justify-between sm:justify-end gap-2 font-orbitron text-[11px] sm:text-xs tracking-[0.18em] sm:tracking-[0.2em] uppercase text-cyan-400/70 group-hover:text-cyan-400 group-hover:gap-3 transition-all duration-300 shrink-0 pl-[22px] sm:pl-0 border-t sm:border-t-0 border-cyan-500/15 pt-3 sm:pt-0">
                  <span className="font-rajdhani sm:hidden text-white/45 normal-case tracking-normal text-sm">
                    Open to senior product &amp; engineering roles
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span>Let's Talk</span>
                    <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                  </span>
                </div>
              </div>
            </Link>
          </BlurReveal>

          {/* Confidential work note */}
          <BlurReveal delay={0.3} className="mt-10 text-center" data-testid="confidential-note">
            <p className="font-rajdhani text-xl md:text-2xl lg:text-3xl text-white/50 tracking-wide leading-relaxed">
              For confidential and enterprise-scale work,{' '}
              <Link
                to="/contact"
                className="text-red-500 font-semibold hover:text-red-400 transition-colors cursor-pointer"
                style={{ textShadow: '0 0 20px rgba(239,68,68,0.4)', textDecoration: 'none' }}
              >
                let's connect directly.
              </Link>
            </p>
          </BlurReveal>
        </section>

        {/* The Arsenal */}
        <section ref={arsenalRef} className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
          <BlurReveal>
            <div className="mb-6">
              <TechnicalAnnotation code="CH · 03 / THE ARSENAL" title="Tools deployed across every engagement" />
            </div>
          </BlurReveal>

          <BlurReveal delay={0.15}>
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter uppercase mt-2 mb-3 leading-[0.95]">
              The <span className="gradient-text">Arsenal</span>
            </h2>
          </BlurReveal>

          <BlurReveal delay={0.22}>
            <p className="text-lg text-white/35 max-w-2xl font-rajdhani leading-relaxed mb-12">
              The protocols, tools, and frameworks deployed across every engagement.
              Depth over breadth — always.
            </p>
          </BlurReveal>

          {/* 8 Category Grid - 4x2 layout */}
          <BlurReveal delay={0.2}>
            <div className="border border-white/10 bg-black/40 backdrop-blur-sm">
            {/* Top Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
              {skillCategories.slice(0, 4).map((category, index) => (
                <motion.div
                  key={category.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.05 * index }}
                  className={`p-6 ${index < 3 ? 'border-r border-white/10' : ''} border-b border-white/10`}
                >
                  <h3 className="font-orbitron text-lg font-bold text-white mb-4 tracking-tight">
                    {category.title}
                  </h3>
                  <ul className="space-y-2">
                    {category.skills.map((skill) => (
                      <li key={skill} className="text-white/60 font-rajdhani text-sm">
                        {skill}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
            
            {/* Bottom Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
              {skillCategories.slice(4, 8).map((category, index) => (
                <motion.div
                  key={category.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.05 * (index + 4) }}
                  className={`p-6 ${index < 3 ? 'border-r border-white/10' : ''}`}
                >
                  <h3 className="font-orbitron text-lg font-bold text-white mb-4 tracking-tight">
                    {category.title}
                  </h3>
                  <ul className="space-y-2">
                    {category.skills.map((skill) => (
                      <li key={skill} className="text-white/60 font-rajdhani text-sm">
                        {skill}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </BlurReveal>
        </section>

        {/* Tools Proficiency */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
          <BlurReveal><SectionLabel>Tools & Technologies</SectionLabel></BlurReveal>

          <BlurReveal delay={0.15}>
            <h3 className="font-headline text-3xl md:text-4xl font-black tracking-tighter uppercase mt-6 mb-10 leading-[0.95]">
              Proficiency <span className="gradient-text">Levels</span>
            </h3>
          </BlurReveal>

          <GlassCard className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
              {tools.map((tool, index) => (
                <motion.div
                  key={tool.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.05 * index }}
                  className="space-y-2"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[11px] text-white/75 tracking-[0.2em] uppercase truncate">
                      {tool.name}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="w-1 h-1 rounded-full bg-[var(--hyper-cyan)] animate-pulse" style={{ boxShadow: '0 0 4px var(--hyper-cyan)' }} />
                      <span className="font-mono text-[11px] text-[var(--hyper-cyan)]">
                        {tool.level}%
                      </span>
                    </span>
                  </div>

                  <div className="h-[3px] bg-white/8 overflow-hidden relative">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${tool.level}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.1 * index, ease: 'easeOut' }}
                      className="h-full bg-gradient-to-r from-[var(--hyper-cyan)] to-[#3B82F6] relative"
                    >
                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_3s_linear_infinite]" style={{ backgroundSize: '200% 100%' }} />
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </GlassCard>
        </section>

        {/* ══════════════════════════════════════════════
            END OF TRANSMISSION — cinematic outro, no boxes,
            lives on the blueprint canvas
        ══════════════════════════════════════════════ */}
        <section
          ref={(el) => { if (el) el.setAttribute('data-scene-signal', ''); }}
          className="relative w-full overflow-visible pt-20 pb-28 sm:pt-28 sm:pb-40"
          data-testid="outro-section"
        >
          {/* Self-drawing isometric signal-tower SVG, painted directly onto
              the canvas — no container, no frame. */}
          <motion.svg
            className="absolute left-1/2 -translate-x-1/2 top-0 pointer-events-none"
            viewBox="0 0 800 360"
            style={{ width: 'min(92vw, 900px)', height: 'auto', opacity: 0.55 }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 0.55 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="sig-stroke" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="var(--scene-accent)" stopOpacity="0.1" />
                <stop offset="0.5" stopColor="var(--scene-accent)" stopOpacity="0.9" />
                <stop offset="1" stopColor="var(--scene-accent)" stopOpacity="0.1" />
              </linearGradient>
            </defs>

            {/* concentric signal arcs radiating outward — self-drawn */}
            {[60, 110, 160, 210, 260].map((r, i) => (
              <motion.path
                key={r}
                d={`M ${400 - r} 300 A ${r} ${r * 0.55} 0 0 1 ${400 + r} 300`}
                fill="none"
                stroke="url(#sig-stroke)"
                strokeWidth={i === 2 ? 1.2 : 0.8}
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 1.8, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] }}
              />
            ))}

            {/* isometric base plate */}
            <motion.path
              d="M 330 300 L 400 282 L 470 300 L 400 318 Z"
              fill="none"
              stroke="var(--scene-accent)"
              strokeWidth="1"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.1 }}
            />

            {/* antenna mast */}
            <motion.line
              x1="400" y1="290" x2="400" y2="150"
              stroke="var(--scene-accent)" strokeWidth="1.2"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.45 }}
            />

            {/* mast cross-bars */}
            {[170, 195, 220, 245, 270].map((y) => (
              <motion.line
                key={y}
                x1={400 - (300 - y) * 0.18} y1={y}
                x2={400 + (300 - y) * 0.18} y2={y}
                stroke="var(--scene-accent)" strokeWidth="0.7"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.55 + (300 - y) * 0.005 }}
              />
            ))}

            {/* transmit dot pulsing at the top */}
            <motion.circle
              cx="400" cy="150" r="3.5"
              fill="var(--scene-accent)"
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 1.25 }}
              style={{ filter: 'drop-shadow(0 0 6px var(--scene-accent))' }}
            />

            {/* tiny ticks on the horizon line for scale */}
            <line x1="60"  y1="300" x2="740" y2="300" stroke="var(--scene-accent)" strokeOpacity="0.2" strokeWidth="0.6" strokeDasharray="2 6" />
          </motion.svg>

          {/* Typographic outro — no container, no card */}
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center flex flex-col items-center gap-7 sm:gap-9 mt-40 sm:mt-56">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="font-mono text-[10px] sm:text-[11px] tracking-[0.4em] uppercase"
              style={{ color: 'var(--scene-accent)' }}
            >
              — END OF TRANSMISSION —
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 28, filter: 'blur(10px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 1, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
              className="font-headline text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter leading-[0.98]"
            >
              The next signal<br />
              <span className="gradient-text">starts with you.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.8, delay: 1.45 }}
              className="font-rajdhani text-base sm:text-lg text-white/38 max-w-xl leading-relaxed"
            >
              The architecture is deployed. The protocols are live.
              Open the channel — we build from here.
            </motion.p>

            {/* Text-only CTA — leader line + label + arrow, lives on the canvas */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.9, delay: 1.6 }}
            >
              <Link
                to="/contact"
                data-testid="get-in-touch-btn"
                className="group inline-flex items-center gap-3 sm:gap-4 font-mono text-[11px] sm:text-xs tracking-[0.38em] uppercase text-white/80 hover:text-white transition-colors"
              >
                <span
                  className="block h-px bg-current transition-[width] duration-500"
                  style={{ width: 36 }}
                />
                <span className="group-hover:tracking-[0.44em] transition-[letter-spacing] duration-500">
                  Open The Channel
                </span>
                <ArrowUpRight
                  size={14}
                  className="group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform duration-500"
                  style={{ color: 'var(--scene-accent)' }}
                />
              </Link>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Project Modal */}
      <ProjectModal 
        isOpen={selectedProject !== null}
        onClose={() => setSelectedProject(null)}
        projectId={selectedProject}
      />
    </div>
  );
}
